import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { f2 } from '../copyFlow2'
import { v3 } from '../copyV3'
import { rememberPlan } from '../lib/intake'
import { loadSaved, useSavedCount, useSavedStore } from '../lib/saved'
import { inSomeGroup } from '../lib/inGroup'

/**
 * Plan v3 (R-73): four places. Browse (the activities), Saved (everything saved, by city), Itineraries (the days they
 * built), and Settings. Category and listing pages belong to Browse; building a day belongs to Saved; an itinerary
 * page belongs to Itineraries.
 */
// Blind round 3 (ari): a group page belongs to the tab it was opened from (Itineraries or Saved), for the highlight and
// the back link. Kept for this tab's session; Saved when unknown.
const TAB_KEY = 'mg_group_tab'
let lastTab: 'saved' | 'itineraries' | null = null
const keepTab = (t: 'saved' | 'itineraries') => { lastTab = t; try { sessionStorage.setItem(TAB_KEY, t) } catch { /* a convenience */ } }
export function groupHomeTab(): 'saved' | 'itineraries' {
  if (lastTab) return lastTab
  try { return sessionStorage.getItem(TAB_KEY) === 'itineraries' ? 'itineraries' : 'saved' } catch { return 'saved' }
}

export function usePlanNav() {
  const { pathname } = useLocation()
  const m = pathname.match(/^\/itinerary\/([^/]+)/)
  if (m) rememberPlan(m[1])
  const s = useSavedStore()
  const count = useSavedCount()
  // the count in the masthead needs Saved loaded once per visit (anonymous sessions included)
  useEffect(() => { if (!s.entries && !s.loading && !s.error) void loadSaved({ onlyWithSession: true }) }, [s.entries, s.loading, s.error])
  // R-29: the person's groups, once per visit, so cards can offer Add to <group> (never starts a session). The groups code
  // loads only for someone in a group (F5: it stays out of the first download).
  useEffect(() => { if (inSomeGroup()) void import('../lib/groups').then((g) => g.ensureGroups({ onlyWithSession: true })).catch(() => {}) }, [pathname])
  const browse = pathname === '/' || /^\/(category|row|listing)\//.test(pathname) || pathname === '/explore'
  const groupPage = /^\/(groups|g)(\/|$)/.test(pathname)
  const savedOwn = pathname === '/saved' || /^\/build(\/|$)/.test(pathname)
  const itinsOwn = pathname === '/itineraries' || /^\/itinerary\//.test(pathname)
  if (savedOwn && pathname === '/saved') keepTab('saved')
  if (itinsOwn) keepTab('itineraries')
  const home = groupPage ? groupHomeTab() : null
  const saved = savedOwn || home === 'saved'
  const itins = itinsOwn || home === 'itineraries'
  return [
    { to: '/', label: f2('nav.browse'), on: browse },
    { to: '/saved', label: count ? v3('v3.nav_saved_count', { count }) : v3('v3.nav_saved'), on: saved, count },
    { to: '/itineraries', label: v3('v3.nav_itineraries'), on: itins },
    { to: '/settings', label: f2('nav.settings'), on: pathname === '/settings' },
  ]
}
